import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fragment, forwardRef, useEffect, useRef } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, TextField, InputAdornment } from '@mui/material';
import CustomSelect2 from './CustomSelect2';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';
import TableCellRate from './TableCellRate';
import TableCellText from './TableCellText';

// Each plan is one whole plan option - dental/vision are optional add-ons only some
// plans support, matching the rate provider's per-plan config (Dental Rates/Vision Rates).
const PLANS = [
  { planId: 'pacific_cross_blue_royale_plan_a', tierId: 'plan_a', label: 'Plan A', rateField: 'plan_a_with_travel_plus', dentalField: 'dental_plan_a', hasVision: false },
  { planId: 'pacific_cross_blue_royale_plan_b', tierId: 'plan_b', label: 'Plan B', rateField: 'plan_b_with_travel_plus', dentalField: 'dental_plan_b', hasVision: true },
  { planId: 'pacific_cross_blue_royale_plan_c', tierId: 'plan_c', label: 'Plan C', rateField: 'plan_c_with_travel_plus', dentalField: null, hasVision: false },
];

const PacificCrossBlueRoyale_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, selectedPlans, setSelectedPlans,
  response, addToPendingTasks,
  discounts, setDiscounts, loadings, setLoadings,
  computedRates, setComputedRates,
  specificCurrency,
  pacificCrossBlueRoyaleSpecific, setPacificCrossBlueRoyaleSpecific,
  deductibles, setDeductibles,
  selectedOptions, addSelectedOption, setViewSelectedOption,
  setShowImageCopy,
  insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const id = 'pacific_cross_blue_royale';

  const containerRef = useRef(null);

  const config = appConfig?.content?.specifics?.pacific_cross_blue_royale || {};

  const deductible_rates = config?.['Deductibles'] || {};
  const payment_frequency_charges = config?.['Payment Frequency Surcharge'] || {};
  const personal_accident_options = config?.['Personal Accident Options'] || {};

  // Deductible options come from the config's keys, so a rate sheet update shows up
  // here without a code change.
  const deductibleItems = ['Nil', ...Object.keys(deductible_rates)];

  const clientTypeItems = ['Individual', 'Group'];

  const personalAccidentCoverageItems = ['No', ...Object.keys(personal_accident_options)];

  // What TableCellRate prints as a dash - shown until the rates are fetched.
  const NO_RATE = '-';

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, pacificCrossBlueRoyaleSpecific, deductibles[id], discounts[id], loadings[id], clients.length]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'client_type', pacificCrossBlueRoyaleSpecific['client_type']);
      addSelectedOption(id, 'dental_benefit', pacificCrossBlueRoyaleSpecific['dental_benefit']);
      addSelectedOption(id, 'vision_benefit', pacificCrossBlueRoyaleSpecific['vision_benefit']);
      addSelectedOption(id, 'treatment_area_limitation', pacificCrossBlueRoyaleSpecific['treatment_area_limitation']);
      addSelectedOption(id, 'personal_accident_coverage', pacificCrossBlueRoyaleSpecific['personal_accident_coverage']);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
    }, 1000);
  }, [deductibles[id], pacificCrossBlueRoyaleSpecific, discounts[id], loadings[id]]);

  useEffect(() => {
    setViewSelectedOption(id, 'deductible', (option_value) => {
      deductibles[id] = option_value;
      setDeductibles({ ...deductibles });
    });

    setViewSelectedOption(id, 'client_type', (option_value) => {
      pacificCrossBlueRoyaleSpecific['client_type'] = option_value;
      setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
    });

    setViewSelectedOption(id, 'dental_benefit', (option_value) => {
      pacificCrossBlueRoyaleSpecific['dental_benefit'] = option_value;
      setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
    });

    setViewSelectedOption(id, 'vision_benefit', (option_value) => {
      pacificCrossBlueRoyaleSpecific['vision_benefit'] = option_value;
      setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
    });

    setViewSelectedOption(id, 'treatment_area_limitation', (option_value) => {
      pacificCrossBlueRoyaleSpecific['treatment_area_limitation'] = option_value;
      setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
    });

    setViewSelectedOption(id, 'personal_accident_coverage', (option_value) => {
      pacificCrossBlueRoyaleSpecific['personal_accident_coverage'] = option_value;
      setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
    });

    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });
  }, [selectedOptions]);

  // Ticks Plan A the first time this card is shown - once a plan has been ticked
  // (here or restored from a saved quotation) this never overrides it again.
  useEffect(() => {
    if (getSelectedPlans().length === 0) {
      setSelectedPlans([...selectedPlans, PLANS[0].planId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One entry per plan, so the Premiums Summary can compare this insurer's plans against
  // the other insurers' - each entry is what the client actually pays for that plan.
  function getComputedRates() {
    if (!response) return;

    const rates = response?.rates || [];

    const _computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const computedRate = {
        'client': rates[i].client,
        'currency': rates[i].currency
      };

      PLANS.forEach(({ tierId }) => {
        computedRate[tierId] = getFinalPremium(i, tierId);
      });

      _computedRates.push(computedRate);
    }

    return _computedRates;
  }

  function getRate(clientIndex, field) {
    const rate = response?.rates?.[clientIndex]?.[field];

    if (rate === undefined || rate === null) return NO_RATE;

    return rate;
  }

  function isRateAvailable(rate) {
    return !(rate === '' || rate === null || rate === undefined || typeof rate === 'string' || isNaN(rate));
  }

  function getSelectedPlans() {
    return PLANS.filter(({ planId }) => selectedPlans.includes(planId));
  }

  function handleDeductibleChange(value) {
    deductibles[id] = value;
    setDeductibles({ ...deductibles });

    addToPendingTasks([id]);
  }

  function isGroupClientType() {
    return (pacificCrossBlueRoyaleSpecific['client_type'] || clientTypeItems[0]) === 'Group';
  }

  function handleClientTypeChange(value) {
    pacificCrossBlueRoyaleSpecific['client_type'] = value;
    setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
  }

  function isTreatmentAreaLimitationChecked() {
    return pacificCrossBlueRoyaleSpecific?.['treatment_area_limitation'] === true;
  }

  function handleTreatmentAreaLimitationChange(checked) {
    pacificCrossBlueRoyaleSpecific['treatment_area_limitation'] = checked;
    setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });

    addToPendingTasks([id]);
  }

  function isPersonalAccidentCoverageSelected() {
    return !!pacificCrossBlueRoyaleSpecific['personal_accident_coverage'] && pacificCrossBlueRoyaleSpecific['personal_accident_coverage'] !== 'No';
  }

  function handlePersonalAccidentCoverageChange(value) {
    pacificCrossBlueRoyaleSpecific['personal_accident_coverage'] = value;
    setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });

    addToPendingTasks([id]);
  }

  function getPlanRate(clientIndex, plan) {
    return getRate(clientIndex, plan.rateField);
  }

  function getRenewalPercent() {
    const discount = parseFloat(discounts?.[id]) || 0;
    const loading = parseFloat(loadings?.[id]) || 0;

    return (100 - discount + loading) * 0.01;
  }

  function getPremiumAfterDiscount(clientIndex, plan) {
    const premium = getPlanRate(clientIndex, plan);

    if (!isRateAvailable(premium)) return premium;

    return premium * getRenewalPercent();
  }

  function isDentalBenefitChecked() {
    return pacificCrossBlueRoyaleSpecific?.['dental_benefit'] === true;
  }

  function isVisionBenefitChecked() {
    return pacificCrossBlueRoyaleSpecific?.['vision_benefit'] === true;
  }

  function getOptionalRate(clientIndex, benefit, plan) {
    if (benefit === 'dental') {
      if (!isDentalBenefitChecked() || !plan.dentalField) return 0;

      return getRate(clientIndex, plan.dentalField);
    }

    if (benefit === 'vision') {
      if (!isVisionBenefitChecked() || !plan.hasVision || !isGroupClientType()) return 0;

      return getRate(clientIndex, 'vision_plan_b');
    }

    if (benefit === 'personal_accident') {
      if (!isPersonalAccidentCoverageSelected()) return 0;

      return getRate(clientIndex, 'personal_accident_rate');
    }

    return 0;
  }

  function getOptionalPremium(clientIndex, plan) {
    let total = 0;

    ['dental', 'vision', 'personal_accident'].forEach(benefit => {
      const rate = getOptionalRate(clientIndex, benefit, plan);

      if (isRateAvailable(rate)) {
        total += rate;
      }
    });

    return total;
  }

  function getFinalPremium(clientIndex, tierId) {
    const plan = PLANS.find(p => p.tierId === tierId);

    if (!plan) return NO_RATE;

    const premium = getPremiumAfterDiscount(clientIndex, plan);

    if (!isRateAvailable(premium)) return premium;

    return premium + getOptionalPremium(clientIndex, plan);
  }

  // Only Annually is quoted directly by the rate sheet, other frequencies are derived
  // here from the surcharges the rate sheet's config carries.
  function getPremiumPerPaymentFrequency(payment_frequency, tierId, clientIndex = null) {
    const annual = clientIndex === null
      ? getSubtotal(idx => getFinalPremium(idx, tierId))
      : getFinalPremium(clientIndex, tierId);

    if (!isRateAvailable(annual)) return annual;

    const surcharge = payment_frequency_charges[payment_frequency] || 0;

    if (payment_frequency === 'Semi Annually') {
      return (annual + (annual * surcharge)) / 2;
    }

    const installments = {
      'Annually': 1,
      'Quarterly': 4,
      'Monthly': 12
    }[payment_frequency] || 1;

    return (annual + (annual * surcharge)) / installments;
  }

  // Matches the rate sheet's labeling for the Semi Annually row specifically -
  // other payment frequencies keep their default "+X%" label.
  function getPaymentFrequencyLabel(payment_frequency) {
    const percent = ((payment_frequency_charges[payment_frequency] || 0) * 100).toFixed(0);

    if (payment_frequency === 'Semi Annually') {
      return `Semi Annual Premium (${percent}%)`;
    }

    return `${payment_frequency} Premium (+${percent}%)`;
  }

  function getSubtotal(getRateForClient) {
    let subTotal = 0;
    let hasAny = false;

    for (let i = 0; i < clients.length; i++) {
      const rate = getRateForClient(i);

      if (isRateAvailable(rate)) {
        subTotal += rate;
        hasAny = true;
      }
    }

    return hasAny ? subTotal : NO_RATE;
  }

  function getCurrency() {
    return response?.rates?.[0]?.currency || specificCurrency?.[id];
  }

  const _selectedPlans = getSelectedPlans();

  const hasDentalPlan = _selectedPlans.some(plan => !!plan.dentalField);
  const hasVisionPlan = _selectedPlans.some(plan => plan.hasVision);

  // Dental and Vision are priced per plan, so each ticked plan that supports them gets its
  // own row (tagged with the plan once more than one is ticked). Personal Accident is the
  // same rate whichever plan it sits on, so it stays a single row.
  const planSuffix = (plan) => _selectedPlans.length > 1 ? ` (${plan.label})` : '';

  const optionalRows = [
    ...(isDentalBenefitChecked() ? _selectedPlans.filter(plan => !!plan.dentalField) : [])
      .map(plan => ({ key: `dental_${plan.tierId}`, benefit: 'dental', plan, label: `Dental${planSuffix(plan)}` })),
    ...(isVisionBenefitChecked() && isGroupClientType() ? _selectedPlans.filter(plan => plan.hasVision) : [])
      .map(plan => ({ key: `vision_${plan.tierId}`, benefit: 'vision', plan, label: `Vision${planSuffix(plan)}` })),
    ...(isPersonalAccidentCoverageSelected() && _selectedPlans.length > 0
      ? [{ key: 'personal_accident', benefit: 'personal_accident', plan: _selectedPlans[0], label: 'Personal Accident' }]
      : [])
  ];

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#1565c0', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#1565c0',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            height: 60
          }}>Pacific Cross Blue Royale</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 4 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
              alt="Pacific Cross"
              style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>

          <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
                {
                  PLANS.map(({ planId, label }) => (
                    <FormControlLabel
                      key={planId}
                      sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                      control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={planId} />}
                      label={label}
                    />
                  ))
                }
              </Box>

              <Box>
                <Typography fontSize={15} fontWeight={600}>Deductible</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 190 }}
                  value={deductibles?.[id] || deductibleItems[0] || ''}
                  onChange={(e) => handleDeductibleChange(e.target.value)}
                  items={deductibleItems}
                  disabled={loadingStatus[id]}
                />
              </Box>

              <Box>
                <Typography fontSize={15} fontWeight={600}>Client Type</Typography>
                {
                  clientTypeItems.map(client_type => (
                    <FormControlLabel
                      key={client_type}
                      sx={{ mb: 0, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                      control={
                        <Checkbox
                          size="small"
                          checked={(pacificCrossBlueRoyaleSpecific['client_type'] || clientTypeItems[0]) === client_type}
                          onChange={() => handleClientTypeChange(client_type)}
                          disabled={loadingStatus[id]}
                        />
                      }
                      label={client_type}
                    />
                  ))
                }
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box>
                <FormControlLabel
                  sx={{ mb: 1, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={isTreatmentAreaLimitationChecked()}
                      onChange={(e) => handleTreatmentAreaLimitationChange(e.target.checked)}
                      disabled={loadingStatus[id]}
                    />
                  }
                  label="Treatment Area Limitation"
                />

                {
                  hasDentalPlan &&
                  <FormControlLabel
                    sx={{ mb: 1, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={isDentalBenefitChecked()}
                        onChange={(e) => {
                          pacificCrossBlueRoyaleSpecific['dental_benefit'] = e.target.checked;
                          setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
                        }}
                        disabled={loadingStatus[id]}
                      />
                    }
                    label="Dental Benefits"
                  />
                }

                {
                  hasVisionPlan && isGroupClientType() &&
                  <FormControlLabel
                    sx={{ mb: 0, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={isVisionBenefitChecked()}
                        onChange={(e) => {
                          pacificCrossBlueRoyaleSpecific['vision_benefit'] = e.target.checked;
                          setPacificCrossBlueRoyaleSpecific({ ...pacificCrossBlueRoyaleSpecific });
                        }}
                        disabled={loadingStatus[id]}
                      />
                    }
                    label="Vision Benefits"
                  />
                }
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Personal Accident Coverage</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 140 }}
                  value={pacificCrossBlueRoyaleSpecific['personal_accident_coverage'] || 'No'}
                  onChange={(e) => handlePersonalAccidentCoverageChange(e.target.value)}
                  items={personalAccidentCoverageItems}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts?.[id] ?? 0}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings?.[id] ?? 0}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7, marginTop: 24 }}>
            <CopyExcelDataButton
              elementId='table-pacific-cross-blue-royale-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-pacific-cross-blue-royale-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-pacific-cross-blue-royale-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#1565c0' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}>
                        <div>{client.full_name} ({client.age})</div>
                        <div>{client.country_of_residence}</div>
                      </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  _selectedPlans.map(plan => (
                    <TableRow key={plan.planId}>
                      <TableCellText content={plan.label} style={{ width: 260, minWidth: 260 }} />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getPlanRate(idx, plan)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getPlanRate(clientIndex, plan))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                      />
                    </TableRow>
                  ))
                }

                {
                  optionalRows.length > 0 &&
                  <TableRow>
                    <TableCell colSpan={clients.length + 2} style={{ fontWeight: 600, background: '#1565c0', color: '#fff' }}>Optional Premium</TableCell>
                  </TableRow>
                }

                {
                  optionalRows.map(({ key, benefit, plan, label }) => (
                    <TableRow key={key}>
                      <TableCellText content={label} style={{ width: 260, minWidth: 260 }} />
                      {
                        clients.map((client, idx) => (
                          <TableCellRate
                            key={client.uid}
                            rate={getOptionalRate(idx, benefit, plan)}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                          />
                        ))
                      }
                      <TableCellRate
                        rate={getSubtotal(clientIndex => getOptionalRate(clientIndex, benefit, plan))}
                        currency={getCurrency()}
                        isLoading={loadingStatus[id]}
                      />
                    </TableRow>
                  ))
                }

                {/* One Annual + payment frequency block per ticked plan, separated by a blank row. */}
                {
                  _selectedPlans.map(({ planId, tierId, label }, planIndex) => (
                    <Fragment key={planId}>
                      {
                        planIndex > 0 &&
                        <TableRow>
                          <TableCell colSpan={clients.length + 2} style={{ height: 20, padding: 0, background: '#fff' }} />
                        </TableRow>
                      }

                      <TableRow>
                        <TableCellText content={`${label} Annual Premium`} style={{ fontWeight: 600 }} bgColor='#ffcc80' />
                        {
                          clients.map((client, idx) => (
                            <TableCellRate
                              key={client.uid}
                              rate={getFinalPremium(idx, tierId)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          ))
                        }
                        <TableCellRate
                          rate={getSubtotal(clientIndex => getFinalPremium(clientIndex, tierId))}
                          currency={getCurrency()}
                          isLoading={loadingStatus[id]}
                          bgColor='#ffcc80'
                        />
                      </TableRow>

                      {
                        Object.keys(payment_frequency_charges).filter(payment_frequency => payment_frequency !== 'Annually').map(payment_frequency => (
                          <TableRow key={payment_frequency}>
                            <TableCellText
                              content={`${label} ${getPaymentFrequencyLabel(payment_frequency)}`}
                              style={{ fontWeight: 600 }}
                              bgColor='#ffcc80'
                            />
                            {
                              clients.map((client, idx) => (
                                <TableCellRate
                                  key={client.uid}
                                  rate={getPremiumPerPaymentFrequency(payment_frequency, tierId, idx)}
                                  currency={getCurrency()}
                                  isLoading={loadingStatus[id]}
                                  bgColor='#ffcc80'
                                />
                              ))
                            }
                            <TableCellRate
                              rate={getPremiumPerPaymentFrequency(payment_frequency, tierId)}
                              currency={getCurrency()}
                              isLoading={loadingStatus[id]}
                              bgColor='#ffcc80'
                            />
                          </TableRow>
                        ))
                      }
                    </Fragment>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default PacificCrossBlueRoyale_Rates;
